import { useState, useCallback, useEffect, useRef } from 'react';
import { TrancheData, AutoAllocatingTranche } from 'further-types/allocations';
import { isValid } from 'date-fns';
import { CalendarDay } from 'further-ui/utils';

interface UseTrancheFormStateParams {
  selectedTrancheIds: Array<string>;
  allocationDate: CalendarDay | null;
  autoAllocatingTranches: Array<AutoAllocatingTranche>;
  fetchTrancheData: (params: {
    trancheIds: Array<string>;
    allocationDate: CalendarDay;
  }) => Promise<Array<TrancheData> | undefined>;
  onUpdate?: (index: number, value: AutoAllocatingTranche) => void;
  onAppend?: (value: Array<AutoAllocatingTranche>) => void;
  preservedAutoAllocationInput?: Array<{
    trancheId: string;
    minimumInvestorPortfolioSize?: number;
    maximumSingleCompanyAllocation?: number;
    targetShares: number;
    transactionFeePercent?: number;
    defaultTransactionFeeVatPercent?: number;
    finalDeal: boolean;
  }>;
}

interface UseTrancheFormStateReturn {
  isLoading: boolean;
  updateTrancheFormState: (
    currentFields: Array<AutoAllocatingTranche>,
  ) => Promise<void>;
}

const useTrancheFormState = ({
  selectedTrancheIds,
  allocationDate,
  autoAllocatingTranches,
  fetchTrancheData,
  onUpdate,
  onAppend,
  preservedAutoAllocationInput = [],
}: UseTrancheFormStateParams): UseTrancheFormStateReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const updateTrancheFormState = useCallback(
    async (currentFields: Array<AutoAllocatingTranche>): Promise<void> => {
      // Disable hook if investment date is not available
      if (!allocationDate || !isValid(allocationDate.toUTCDate())) {
        return;
      }

      if (!onUpdate || !onAppend) {
        return;
      }

      const currentTrancheIds = currentFields
        .filter((tranche) => !tranche.isRemoved)
        .map((tranche) => tranche._id);

      // Get all tranche IDs (including removed ones) for checking existing tranches
      const allCurrentTrancheIds = currentFields.map((tranche) => tranche._id);

      // Find new tranche IDs that need to be added
      const newTrancheIds = selectedTrancheIds.filter(
        (id) => !allCurrentTrancheIds.includes(id),
      );

      // Find tranche IDs that need to be removed
      const removedTrancheIds = currentTrancheIds.filter(
        (id) => !selectedTrancheIds.includes(id),
      );

      const myRequestId = ++requestIdRef.current;
      setIsLoading(true);

      try {
        // Mark tranches as removed (preserve all form values)
        removedTrancheIds.forEach((trancheId) => {
          const index = currentFields.findIndex(
            (tranche) => tranche._id === trancheId,
          );
          if (index !== -1) {
            const currentTranche = currentFields[index];
            onUpdate(index, {
              ...currentTranche,
              isRemoved: true,
            });
          }
        });

        // Check for re-adds (tranches that were removed but are now selected again)
        const reAddTrancheIds = selectedTrancheIds.filter((trancheId) => {
          const existingTranche = currentFields.find(
            (tranche) => tranche._id === trancheId,
          );
          return existingTranche?.isRemoved;
        });

        // Re-add previously removed tranches (maintain form values)
        reAddTrancheIds.forEach((trancheId) => {
          const index = currentFields.findIndex(
            (tranche) => tranche._id === trancheId,
          );
          if (index !== -1) {
            const existingTranche = currentFields[index];
            onUpdate(index, { ...existingTranche, isRemoved: false });
          }
        });

        // Handle newly seen tranche IDs
        if (newTrancheIds.length > 0) {
          const newTrancheData = await fetchTrancheData({
            trancheIds: newTrancheIds,
            allocationDate,
          });

          // Only apply results if this is still the latest request
          if (requestIdRef.current !== myRequestId) {
            return;
          }

          if (newTrancheData && newTrancheData.length > 0) {
            // Create lookup from preservedAutoAllocationInput
            const preservedDraftLookup = new Map<
              string,
              {
                minimumInvestorPortfolioSize: number;
                maximumSingleCompanyAllocation: number;
                initialTargetShares: number;
                defaultTransactionFeePercent: number;
                defaultTransactionFeeVatPercent: number;
                finalDeal: boolean;
              }
            >();

            preservedAutoAllocationInput.forEach((fundAllocation) => {
              preservedDraftLookup.set(fundAllocation.trancheId, {
                minimumInvestorPortfolioSize:
                  fundAllocation.minimumInvestorPortfolioSize ?? 0,
                maximumSingleCompanyAllocation:
                  fundAllocation.maximumSingleCompanyAllocation ?? 0,
                initialTargetShares: fundAllocation.targetShares,
                defaultTransactionFeePercent:
                  fundAllocation.transactionFeePercent ?? 0,
                defaultTransactionFeeVatPercent:
                  fundAllocation.defaultTransactionFeeVatPercent ?? 0,
                finalDeal: fundAllocation.finalDeal,
              });
            });

            const newTranches = newTrancheData.map((trancheData, index) => {
              const preservedDraft = preservedDraftLookup.get(trancheData._id);

              return {
                ...trancheData,
                order: currentFields.length + index,
                isRemoved: false,
                // Use preserved draft data if available, otherwise use defaults
                minimumInvestorPortfolioSize:
                  preservedDraft?.minimumInvestorPortfolioSize ??
                  trancheData.minimumInvestorPortfolioSize,
                maximumSingleCompanyAllocation:
                  preservedDraft?.maximumSingleCompanyAllocation ??
                  trancheData.maximumSingleCompanyAllocation,
                initialTargetShares: preservedDraft?.initialTargetShares ?? 0,
                defaultTransactionFeePercent:
                  preservedDraft?.defaultTransactionFeePercent ??
                  trancheData.defaultTransactionFeePercent,
                defaultTransactionFeeVatPercent:
                  preservedDraft?.defaultTransactionFeeVatPercent ??
                  trancheData.defaultTransactionFeeVatPercent,
                finalDeal: preservedDraft?.finalDeal ?? false,
              };
            });

            // Add all new tranches at once
            onAppend(newTranches);
          }
        }
      } catch (error) {
        console.error('Error updating tranche form state', error);
      } finally {
        // Only clear loading if this is still the latest request
        if (requestIdRef.current === myRequestId) {
          setIsLoading(false);
        }
      }
    },
    [selectedTrancheIds],
  );

  // Listen for changes in selectedTrancheIds or form resetting
  useEffect(() => {
    updateTrancheFormState(autoAllocatingTranches);
  }, [selectedTrancheIds]);

  return {
    isLoading,
    updateTrancheFormState,
  };
};

export default useTrancheFormState;
